import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import InvoiceDefaultView from '../invoice-default-view/invoice-default-view'
import InvoiceEditView from '../invoice-edit-view/invoice-edit-view'
import { FormProvider, useForm } from 'react-hook-form'
import {
  InvoiceType,
  isToBeInvoicedWorkspaceTab,
} from 'store/invoices/invoices.types'
import { useListInvoicesByUserQuery } from 'store/invoices/invoices.api'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'store'
import { useGetUserCompanyAssociationByUserQuery } from 'store/company/company.api'
import { getUserId } from 'utils/common'
import {
  factoringCompanyIdSet,
  filterTransformInvoiceGroups,
  filterTransformInvoiceGroupsByPodSubTab,
} from 'pages/invoice/invoice.utils'
import { usePodPresenceForOrders } from 'pages/invoice/use-pod-presence-for-orders'
import { useInvoiceWorkspaceScope } from 'pages/invoice/invoice-workspace.context'
import { clearSelectedCustomerId } from 'store/invoices/invoices.slice'

interface InvoiceDetailsProps {
  tabsValue: number
}

const InvoiceDetails = ({ tabsValue }: InvoiceDetailsProps) => {
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('TO_DELIVER')
  const params = useParams()
  const form = useForm()
  const dispatch = useDispatch()
  const customerScope = useInvoiceWorkspaceScope()
  const userId = getUserId()
  const { data: associations } = useGetUserCompanyAssociationByUserQuery(
    { userId },
    { skip: !userId }
  )
  const factoringIds = useMemo(
    () => (associations !== undefined ? factoringCompanyIdSet(associations) : undefined),
    [associations]
  )

  const { selectedCustomerId } = useSelector(
    (state: RootState) => state.invoices
  )

  const { data: invoices } = useListInvoicesByUserQuery({
    invoiceType,
  })

  const scopedInvoices = useMemo(
    () => filterTransformInvoiceGroups(invoices, customerScope, factoringIds),
    [invoices, customerScope, factoringIds]
  )
  const customerFilteredInvoices = useMemo(() => {
    if (!selectedCustomerId) return scopedInvoices

    return scopedInvoices
      .map((group) => ({
        ...group,
        invoiceData: group.invoiceData.filter(
          (invoice) => invoice.customerId === selectedCustomerId
        ),
      }))
      .filter((group) => group.invoiceData.length > 0)
  }, [scopedInvoices, selectedCustomerId])

  const orderIdsForPod = useMemo(() => {
    if (!isToBeInvoicedWorkspaceTab(invoiceType)) return []
    return customerFilteredInvoices.flatMap((g) =>
      g.invoiceData.map((inv) => inv.orderId).filter((id): id is string => Boolean(id))
    )
  }, [invoiceType, customerFilteredInvoices])

  const podByOrderId = usePodPresenceForOrders(orderIdsForPod)

  const displayInvoices = useMemo(
    () =>
      filterTransformInvoiceGroupsByPodSubTab(
        customerFilteredInvoices,
        invoiceType,
        podByOrderId
      ),
    [customerFilteredInvoices, invoiceType, podByOrderId]
  )

  useEffect(() => {
    if (!selectedCustomerId || !scopedInvoices.length) return
    const stillVisible = scopedInvoices.some((group) =>
      group.invoiceData.some((inv) => inv.customerId === selectedCustomerId)
    )
    if (!stillVisible) {
      dispatch(clearSelectedCustomerId())
    }
  }, [selectedCustomerId, scopedInvoices, dispatch])

  useEffect(() => {
    switch (tabsValue) {
      case 0:
        setInvoiceType('TO_DELIVER')
        break
      case 1:
        setInvoiceType('TO_INVOICE')
        break
      case 2:
        setInvoiceType('NOT_PAID')
        break
      case 3:
        setInvoiceType('PAID')
        break
      default:
        setInvoiceType('TO_DELIVER')
        break
    }
  }, [tabsValue])

  return (
    <FormProvider {...form}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {!!params?.invoiceId ? (
          <InvoiceEditView />
        ) : (
          <InvoiceDefaultView
            key={`${customerScope}-${invoiceType}`}
            invoices={displayInvoices}
            invoiceType={invoiceType}
          />
        )}
      </Box>
    </FormProvider>
  )
}

export default InvoiceDetails
